const CONTEXT_IDS = ["assessmentDate", "assessor", "environment", "analysis"]

export function getOrdinalProfileItems(config = {}) {
	return (config.ordinalProfile?.sections || []).flatMap(section => section.items || [])
}

export function createOrdinalProfileConfig(config = {}) {
	const profile = config.ordinalProfile || {}
	const fields = [
		{id: "assessmentDate", title: profile.dateLabel || "Date", type: "DATE"},
		{id: "assessor", title: profile.assessorLabel || "Assessor", type: "TEXT"},
		{id: "environment", title: profile.environmentLabel || "Environment", type: "TEXT"},
		{id: "analysis", title: profile.analysisLabel || "Analysis", type: "MULTILINE"},
	]
	for (const item of getOrdinalProfileItems(config)) {
		fields.push({id: item.id, title: item.question || item.title, type: "TEXT"})
		fields.push({id: `${item.id}Comment`, title: profile.commentLabel || "Comments", type: "MULTILINE"})
		if (profile.informationSources) {
			for (const suffix of ["Reported", "Observed", "SourceLimit", "Interpretation", "Supports", "Barriers", "Patterns"]) {
				fields.push({id: `${item.id}${suffix}`, title: profile[`${suffix.charAt(0).toLowerCase()}${suffix.slice(1)}Label`] || suffix, type: "MULTILINE"})
			}
		}
	}
	if (profile.informationSources) fields.push({id: "profileMode", title: profile.profileModeLabel || "Profile type", type: "TEXT"})
	return {...config, fields}
}

export function getOrdinalProfileConfigErrors(config = {}) {
	const profile = config.ordinalProfile
	if (!profile || !Array.isArray(profile.sections) || !Array.isArray(profile.options) || profile.options.length < 2) return ["Invalid profile configuration"]
	const ids = [...CONTEXT_IDS]
	const options = profile.options
	if (options.some(option => !option.id || !option.title) || new Set(options.map(option => option.id)).size !== options.length) return ["Invalid rating options"]
	for (const section of profile.sections) {
		if (!section.id || !section.title || !Array.isArray(section.items)) return ["Invalid profile section"]
		ids.push(section.id)
		for (const item of section.items) {
			if (!item.id || !item.title || !item.question) return ["Invalid profile item"]
		ids.push(item.id, `${item.id}Comment`)
		if (profile.informationSources) for (const suffix of ["Reported", "Observed", "SourceLimit", "Interpretation", "Supports", "Barriers", "Patterns"]) ids.push(`${item.id}${suffix}`)
		}
	}
	if (new Set(ids).size !== ids.length) return ["Duplicate profile identifiers"]
	if (!Number.isInteger(profile.maxUnrated) || profile.maxUnrated < 0) return ["Invalid unrated allowance"]
	if (profile.ratingPresentation === "stars") {
		if (profile.ratingScaleMax !== profile.options.length || profile.ratingScaleMax < 3 || profile.ratingScaleMax > 10) return ["Invalid rating scale maximum"]
		if (profile.options.some(option => !Number.isFinite(Number(option.value))) || new Set(profile.options.map(option => Number(option.value))).size !== profile.options.length) return ["Rating choices need distinct numeric values"]
	}
	return []
}

export function getOrdinalProfileStatus(config = {}, answers = {}) {
	const items = getOrdinalProfileItems(config)
	const selectedIds = new Set((config.ordinalProfile?.options || []).map(option => option.id))
	const missing = items.filter(item => !selectedIds.has(answers[item.id]))
	const invalid = missing.filter(item => answers[item.id] !== null && answers[item.id] !== undefined && answers[item.id] !== "")
	const undocumented = missing.filter(item => !String(answers[`${item.id}Comment`] || "").trim())
	const fullyRated = items.length > 0 && missing.length === 0
	const hasInformation = items.some(item => [item.id, `${item.id}Reported`, `${item.id}Observed`, `${item.id}SourceLimit`, `${item.id}Interpretation`, `${item.id}Supports`, `${item.id}Barriers`, `${item.id}Patterns`].some(id => answers[id] !== null && answers[id] !== undefined && String(answers[id]).trim() !== ""))
	const complete = items.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(answers.assessmentDate || "") && (config.ordinalProfile.allowPartialCompletion ? hasInformation && !invalid.length : missing.length <= config.ordinalProfile.maxUnrated && !undocumented.length && !invalid.length)
	return {complete, fullyRated, answered: items.length - missing.length, total: items.length, missingIds: missing.map(item => item.id), undocumentedIds: undocumented.map(item => item.id), invalidIds: invalid.map(item => item.id)}
}

export function getOrdinalProfilePayloadErrors(payload = {}, config = {}) {
	const errors = getOrdinalProfileConfigErrors(config)
	if (errors.length) return errors
	const answers = payload.answers || {}
	const optionIds = new Set(config.ordinalProfile.options.map(option => option.id))
	for (const item of getOrdinalProfileItems(config)) {
		const value = answers[item.id]
		if (value !== null && value !== undefined && value !== "" && !optionIds.has(value)) return ["Invalid profile rating"]
	}
	if (!getOrdinalProfileStatus(config, answers).complete) return ["Screening is incomplete"]
	return []
}

export function createOrdinalProfileSummary(payload = {}, config = {}) {
	const form = createOrdinalProfileConfig(config)
	const answers = {...payload.answers}
	const status = getOrdinalProfileStatus(form, answers)
	return {headline: `${status.answered}/${status.total} items rated`, form, answers, archivedItems: payload.archivedItems || [], profileStatus: status, scoreResult: null}
}

export function getArchivedOrdinalProfileItems(payload = {}, config = {}) {
	const activeIds = new Set(getOrdinalProfileItems(config).map(item => item.id))
	const previousConfig = payload.form || {}
	const candidates = [...(payload.archivedItems || []), ...getOrdinalProfileItems(previousConfig).map(item => ({...item, options: previousConfig.ordinalProfile?.options || []}))]
	const archived = new Map()
	for (const item of candidates) {
		const hasAnswer = [item.id, `${item.id}Comment`, `${item.id}Reported`, `${item.id}Observed`, `${item.id}SourceLimit`, `${item.id}Interpretation`, `${item.id}Supports`, `${item.id}Barriers`, `${item.id}Patterns`].some(id => Object.prototype.hasOwnProperty.call(payload.answers || {}, id))
		if (!activeIds.has(item.id) && hasAnswer) archived.set(item.id, item)
	}
	return [...archived.values()]
}

export function getOrdinalProfileComparisons(config = {}, priorResults = []) {
	const currentItems = getOrdinalProfileItems(config)
	return priorResults.map(result => {
		const priorConfig = result.enginePayload?.form || result.summary?.form || {}
		const priorAnswers = result.enginePayload?.answers || result.summary?.answers || {}
		const priorItems = new Map(getOrdinalProfileItems(priorConfig).map(item => [item.id, item]))
		const comparableItems = currentItems.filter(item => Object.prototype.hasOwnProperty.call(priorAnswers, item.id) || priorItems.has(item.id))
		const options = config.ordinalProfile?.options || []
		const previousOptions = priorConfig.ordinalProfile?.options || []
		const ratingLabels = Object.fromEntries(comparableItems.map(item => {
			const value = priorAnswers[item.id]
			const current = options.find(option => option.id === value)
			const previous = previousOptions.find(option => option.id === value)
			const prefix = current?.value !== undefined && config.ordinalProfile?.ratingPresentation === "stars" ? `${current.value} — ` : ""
			const label = value === undefined || value === null || value === "" ? "Not rated" : current ? `${prefix}${current.title}` : `${previous?.title || value} (removed choice)`
			return [item.id, label]
		}))
		return {id: result.id, date: priorAnswers.assessmentDate || "Date unavailable", environment: priorAnswers.environment || "", comparableItems, ratings: priorAnswers, ratingLabels, hasDifferences: comparableItems.length !== currentItems.length || currentItems.length !== priorItems.size, complete: getOrdinalProfileStatus(config, priorAnswers).complete}
	})
}

export function getOrdinalProfileDetails(config = {}, answers = {}, includeEmpty = false) {
	const profile = config.ordinalProfile || {}
	const sourceKeys = [
		["Reported", "reportedLabel"], ["Observed", "observedLabel"], ["SourceLimit", "sourceLimitLabel"],
		["Interpretation", "interpretationLabel"], ["Supports", "supportsLabel"],
		["Barriers", "barriersLabel"], ["Patterns", "patternsLabel"],
	]
	return (profile.sections || []).map(section => ({...section, items: (section.items || []).map(item => {
		const selected = profile.options?.find(option => option.id === answers[item.id])
		const archivedChoice = item.options?.find(option => option.id === answers[item.id])
		const sources = profile.informationSources ? sourceKeys.map(([suffix, labelKey]) => ({label: profile[labelKey] || suffix, value: answers[`${item.id}${suffix}`] || ""})).filter(source => includeEmpty || source.value) : []
		const previousRating = answers[item.id] === null || answers[item.id] === undefined || answers[item.id] === "" ? "Not rated" : `${archivedChoice?.title || answers[item.id]} (removed choice)`
		return {...item, rating: selected ? `${selected.value ? `${selected.value} — ` : ""}${selected.title}` : previousRating, sources, comment: answers[`${item.id}Comment`] || ""}
	})}))
}
